import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ["Domestic Cleaner", "Deep Clean Specialist", "Commercial Cleaner", "Team Leader"];

const inputCls =
  "w-full rounded-xl border border-[#1E3B30] bg-[#0F382A]/40 px-4 py-3.5 text-sm text-[#FAF8F5] placeholder-[#FAF8F5]/40 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#D4AF37] focus:shadow-[0_0_0_3px_rgba(212,175,55,0.12)]";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-[#FAF8F5]/50";

export default function Careers() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: ROLES[0], availability: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/applications`, {
        ...form,
        availability: form.availability || null,
        message: form.message || null,
      });
      toast.success("Application received — we'll be in touch within two working days.");
      setForm({ name: "", email: "", phone: "", role: ROLES[0], availability: "", message: "" });
    } catch {
      toast.error("Something went wrong — please try again or call 07823 945998.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="careers" data-testid="careers-section" className="bg-[#0B1D16] py-24 lg:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="lg:col-span-5"
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
            Careers
          </p>
          <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#FAF8F5] sm:text-5xl lg:text-6xl">
            Join the
            <br />
            <em className="italic text-[#D4AF37]">Sparkle team.</em>
          </h2>
          <p className="mt-6 max-w-sm text-base leading-relaxed text-[#FAF8F5]/60">
            We're always looking for meticulous cleaners across Harlow and
            Essex. Flexible hours, fair pay, full training — and a team that
            takes pride in the finish.
          </p>
          <ul className="mt-8 space-y-3">
            {["Flexible daytime schedules", "All equipment & eco products provided", "Paid travel between jobs"].map((perk) => (
              <li key={perk} className="flex items-start gap-2.5 text-sm text-[#FAF8F5]/70">
                <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[#D4AF37]" />
                {perk}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.form
          data-testid="career-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.12, ease: EASE }}
          className="rounded-[2rem] border border-[#1E3B30] bg-[#0F382A]/30 p-8 lg:col-span-7 lg:p-12"
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="career-name">Full name</label>
              <input id="career-name" data-testid="career-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="career-phone">Phone</label>
              <input id="career-phone" data-testid="career-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="07xxx xxxxxx" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="career-email">Email</label>
              <input id="career-email" data-testid="career-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="career-role">Role</label>
              <select id="career-role" data-testid="career-role-select" value={form.role} onChange={set("role")} className={inputCls}>
                {ROLES.map((r) => (
                  <option key={r} value={r} className="bg-[#0B1D16]">{r}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="career-availability">Availability</label>
              <input id="career-availability" data-testid="career-availability-input" value={form.availability} onChange={set("availability")} placeholder="e.g. Weekdays 9am–3pm" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="career-message">Tell us about yourself</label>
              <textarea id="career-message" data-testid="career-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Experience, areas you can cover…" className={`${inputCls} resize-none`} />
            </div>
          </div>
          <button
            data-testid="career-submit-button"
            type="submit"
            disabled={sending}
            className="mt-8 w-full rounded-full bg-[#D4AF37] py-4 text-sm font-semibold text-[#0B1D16] transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-[#e3c153] disabled:opacity-60 sm:w-auto sm:px-12"
          >
            {sending ? "Sending…" : "Apply now"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
